/**
 * Lane C0-PROPORTION: the B3 figure (user decision 2026-10-10: one proportion for every figure, about 5.2 heads at
 * today's height, a visible neck), behind a temporary build flag. With `FIGURE_B3` on, rig/sets.ts `frameOf` draws
 * every rig frame here:
 * - the skeleton: every joint lengthened from the soles (`k`), the neck and head raised over the shoulders (`n`), the
 *   head set back on the neck by the head scale (`s`);
 * - the head's art (the head stamp, hair, headwear, the face's art-resolution parts: jaw cells, facial hair) shrunk
 *   by `s` at art resolution about the head joint's pixel (a coverage vote; tails keep their length), inside the one
 *   compose, so depth and the native 2x painters work as today on the smaller shapes;
 * - the face's features drawn at B3 size at the fine grid (b3/faceB3.ts);
 * - the mask, veil, crown and hairpin redrawn at B3 size (b3/headB3.ts).
 * Pilot, QC and the port plan: docs/research/C0_PROPORTION.md. The flag and the old path go once the port is done.
 */
import type { Pose, View8 } from '../../sprites';
import { compose, type Layer, type Piece, RIG_RES } from '../figure';
import { GROUND, type Joint, type JointName, type Skeleton, type BodyShape, skeleton } from '../skeleton';
import { newSkeleton } from '../newBody';
import { clothAt, withCloth } from '../cloth';
import { litFinish } from '../finish';
import { leftFinish } from '../dir8';
import { cropFrame, toArtRes } from '../../pixel';
import { density1x } from '../../density';
import { bladeLines, type BladeLine } from '../weapons';
import type { Appearance } from '../sets';
import { faceLayerB3, FACE_S } from './faceB3';
import { shade } from '../../palette';
import type { Expr } from '../faces2/parts';
import { crownB3, hairpinB3, REDRAWN_HEADWEAR } from './headB3';
import { heroSkullFor, heroSkullPiece, neckWidth, restoreSkull } from './skullB3';
import { withFineHands } from './handsB3';
import { bodyMock, bodyMockOf, fillHoles, setBodyTarget, mockExtras, mockFarLeg, mockGarment, mockHairBack, mockHands, mockNeck, mockProfile, mockSkullNeck } from './bodyMockB3';

export { FIGURE_B3, figureB3, setFigureB3 } from './flag';

export interface B3 {
  /** Head scale. */
  s: number;
  /** Length of everything below the head, from the soles. */
  k: number;
  /** Neck lift, art px. */
  n: number;
  /** Body width (shoulders, chest, waist, hips, limb thickness), so the mass matches the smaller head. */
  w: number;
  /** A child's body (arms a pixel longer). */
  child?: boolean;
}

/** Body width of B3 against today (user 2026-10-11: "the head became small and the clothes too big"). */
let WIDTH = 0.85;
/** Review tools only: try another body width (clears the cached numbers). */
export function setB3Width(w: number): void {
  WIDTH = w;
  PARAMS.clear();
}

/**
 * The numbers for a body: B3 (head ×0.75, neck lifted a pixel) at the body's own height; a child keeps a larger head
 * (×0.88, half the lift). `k` makes the standing figure as tall as today.
 */
const PARAMS = new Map<string, B3>();
export function b3Of(body: BodyShape): B3 {
  const key = `${WIDTH}|${body.age ?? ''}|${body.female ? 1 : 0}|${body.build}|${body.height}|${body.grown ? 1 : 0}`;
  const hit = PARAMS.get(key);
  if (hit) return hit;
  const child = body.age === 'child';
  const s = child ? 0.88 : FACE_S;
  const n = child ? 0.5 : 1;
  const sk = newSkeleton(skeleton('front', 'idle', 0, 0, { ...body, stanced: false }));
  const crown = sk.joints.head.y - 11;
  const neck = sk.joints.neck.y;
  const G = GROUND + 1;
  const k = 1 + ((1 - s) * (neck - crown) - n) / (G - neck);
  // Children keep their own build; elders narrow a little less (some mass and stoop stay).
  const w = child ? 1 - (1 - WIDTH) * 0.5 : body.age === 'elder' ? 1 - (1 - WIDTH) * 0.8 : WIDTH;
  const p = { s, k, n, w, child };
  PARAMS.set(key, p);
  return p;
}

/** A pose's skeleton at B3. */
export function b3Skeleton(sk: Skeleton, p: B3): Skeleton {
  const G = GROUND + 1;
  const joints = {} as Record<JointName, Joint>;
  for (const [name, j] of Object.entries(sk.joints) as [JointName, Joint][]) joints[name] = { ...j, y: G - (G - j.y) * p.k };
  const nk = sk.joints.neck;
  const hd = sk.joints.head;
  joints.neck = { ...joints.neck, y: joints.neck.y - p.n };
  joints.head = { ...hd, x: joints.neck.x + (hd.x - nk.x) * p.s, y: joints.neck.y + (hd.y - nk.y) * p.s };
  if (p.child) {
    // A child's arms a pixel longer (ns-art B3: they ended at the hem).
    for (const side of ['A', 'B'] as const)
      for (const [n, d] of [[`elbow${side}`, 0.4], [`wrist${side}`, 1], [`hand${side}`, 1]] as const)
        joints[n] = { ...joints[n], y: joints[n].y + d };
  }
  if (p.w === 1) return { ...sk, joints };
  // Narrower: each shoulder in toward the chest and each hip toward the pelvis, the arm and leg moved with it (their
  // pose kept), and the build's half-widths and limb thickness brought down by the same factor.
  const w = p.w;
  const shift = (from: JointName, centre: JointName, chain: readonly JointName[]) => {
    const dx = (joints[from].x - joints[centre].x) * (w - 1);
    for (const n of chain) joints[n] = { ...joints[n], x: joints[n].x + dx };
  };
  for (const side of ['A', 'B'] as const) {
    shift(`shoulder${side}`, 'chest', [`shoulder${side}`, `elbow${side}`, `wrist${side}`, `hand${side}`]);
    shift(`hip${side}`, 'pelvis', [`hip${side}`, `knee${side}`, `ankle${side}`]);
  }
  const sh = sk.shape;
  const shape = { ...sh, shoulder: sh.shoulder * w, waist: sh.waist * w, hip: sh.hip * w, limb: (5 + sh.limb) * w - 5 };
  return { ...sk, joints, shape };
}

// ── the head's art, shrunk ──

const VC = new Uint32Array(16);
const VW = new Float64Array(16);

let tmp: CanvasRenderingContext2D | null = null;
function scratch(w: number, h: number): CanvasRenderingContext2D {
  if (!tmp || tmp.canvas.width !== w || tmp.canvas.height !== h) {
    const c = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h });
    tmp = (c as HTMLCanvasElement).getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
  }
  return tmp;
}

/**
 * A piece's drawing brought down about the head joint's pixel by `sx`, `sy` at art resolution: each new pixel takes
 * the colour covering most of it (drawn pixels count a little more, so 1-px lines hold; ties go to the darker).
 */
function shrunk(draw: (g: CanvasRenderingContext2D) => void, sk: Skeleton, sx: number, sy: number): (g: CanvasRenderingContext2D) => void {
  return (g) => {
    const W = g.canvas.width;
    const H = g.canvas.height;
    const t = g.getTransform();
    const s = scratch(W, H);
    s.setTransform(1, 0, 0, 1, 0, 0);
    s.clearRect(0, 0, W, H);
    s.setTransform(t);
    draw(s);
    s.setTransform(1, 0, 0, 1, 0, 0);
    const src = new Uint32Array(s.getImageData(0, 0, W, H).data.buffer);
    let x0 = W;
    let y0 = H;
    let x1 = -1;
    let y1 = -1;
    for (let i = 0; i < W * H; i++)
      if (src[i]! >>> 24) {
        const x = i % W;
        const y = (i - x) / W;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    if (x1 < 0) return;
    const P = t.transformPoint({ x: Math.round(sk.joints.head.x), y: Math.round(sk.joints.head.y) });
    const px = P.x;
    const py = P.y;
    const dx0 = Math.floor(px + (x0 - px) * sx) - 1;
    const dx1 = Math.ceil(px + (x1 + 1 - px) * sx) + 1;
    const dy0 = Math.floor(py + (y0 - py) * sy) - 1;
    const dy1 = Math.ceil(py + (y1 + 1 - py) * sy) + 1;
    const ow = dx1 - dx0;
    const oh = dy1 - dy0;
    const out = new ImageData(ow, oh);
    const od = new Uint32Array(out.data.buffer);
    // A footprint covers at most 3×3 source pixels (scale ≥ 0.5): votes in small fixed arrays, no allocation.
    const vc = VC;
    const vw = VW;
    for (let y = dy0; y < dy1; y++) {
      const fy0 = py + (y - py) / sy;
      const fy1 = py + (y + 1 - py) / sy;
      for (let x = dx0; x < dx1; x++) {
        const fx0 = px + (x - px) / sx;
        const fx1 = px + (x + 1 - px) / sx;
        let n = 0;
        for (let yy = Math.floor(fy0); yy < Math.ceil(fy1); yy++) {
          if (yy < 0 || yy >= H) continue;
          const ay = Math.min(yy + 1, fy1) - Math.max(yy, fy0);
          for (let xx = Math.floor(fx0); xx < Math.ceil(fx1); xx++) {
            if (xx < 0 || xx >= W) continue;
            const a = (Math.min(xx + 1, fx1) - Math.max(xx, fx0)) * ay;
            if (a <= 0) continue;
            const c = src[yy * W + xx]!;
            const key = c >>> 24 ? c : 0;
            const w = a * (key ? 1.15 : 1);
            let k = 0;
            while (k < n && vc[k] !== key) k++;
            if (k === n) {
              vc[n] = key;
              vw[n++] = w;
            } else vw[k]! += w;
          }
        }
        let best = 0;
        let top = -1;
        for (let k = 0; k < n; k++) {
          const c = vc[k]!;
          const w = vw[k]!;
          if (w > top + 1e-9) [best, top] = [c, w];
          else if (Math.abs(w - top) <= 1e-9 && c) {
            const lum = (c & 255) + ((c >>> 8) & 255) + ((c >>> 16) & 255);
            const blum = (best & 255) + ((best >>> 8) & 255) + ((best >>> 16) & 255);
            if (!best || lum < blum) [best, top] = [c, w];
          }
        }
        od[(y - dy0) * ow + (x - dx0)] = best;
      }
    }
    // Laid over what is there, pixel by pixel on the CPU (no GPU round trip through drawImage).
    const cx0 = Math.max(0, dx0);
    const cy0 = Math.max(0, dy0);
    const cw = Math.min(W, dx1) - cx0;
    const chh = Math.min(H, dy1) - cy0;
    if (cw <= 0 || chh <= 0) return;
    const region = g.getImageData(cx0, cy0, cw, chh);
    const rd = new Uint32Array(region.data.buffer);
    for (let y = 0; y < chh; y++)
      for (let x = 0; x < cw; x++) {
        const c = od[(y + cy0 - dy0) * ow + (x + cx0 - dx0)]!;
        if (c >>> 24) rd[y * cw + x] = c;
      }
    g.putImageData(region, cx0, cy0);
  };
}

/** A head piece: drawn shrunk; a hanging tail keeps its length (only narrower). */
function headPiece(p: Piece, sk: Skeleton, s: number, tail = false): Piece {
  return { ...p, draw: shrunk(p.draw, sk, s, tail ? 1 : s) };
}

// ── shoulders ──

let SLOPE = true;
/** Review tools only: the sloped shoulders on (default) or off. */
export function setB3Slope(v: boolean): void {
  SLOPE = v;
}

/**
 * The columns cut off the top of each hanging arm's shoulder (art px): from the neck's edge a trapezius line falls
 * gently, then rounds down over the deltoid, so every garment over it (robes, sleeves, collars) drapes
 * over the shoulder instead of ending in a box corner (the bare body already slopes, with a deltoid: newBody.ts; the
 * box corner came from the garments, whose torso and sleeves square off at the shoulder). Women's fall a pixel further. Head-on, three-quarter and from
 * behind; in profile the shoulder is seen end-on and keeps its shape; a raised arm keeps its shoulder.
 */
function shoulderCut(sk: Skeleton, female: boolean): [number, number, number][] {
  const j = sk.joints;
  const out: [number, number, number][] = [];
  const topY = Math.round(j.chest.y) - 1;
  const drop = female ? 5 : 4;
  // Any hand or wrist brought up into the shoulder band keeps both shoulders: a forearm crossing over to the other
  // side would be cut by that side's line (QA B3-R2 N1: hurt, qi, wxQi).
  for (const s of ['A', 'B'] as const)
    if (j[`wrist${s}`].y < topY + drop + 3 || j[`hand${s}`].y < topY + drop + 3) return out;
  for (const side of ['A', 'B'] as const) {
    const sh = j[`shoulder${side}`];
    const el = j[`elbow${side}`];
    // A raised arm, or a forearm brought up to the head or across the shoulder, keeps its shoulder (motion pass:
    // hurt, qi and wxQi lost the hand's sleeve to the cut).
    const wr = j[`wrist${side}`];
    const hd = j[`hand${side}`];
    if (el.y < sh.y + 2 || wr.y < sh.y + 4 || hd.y < sh.y + 4) continue;
    const off = sh.x - j.chest.x;
    if (Math.abs(off) < 1.5) continue;
    const dir = Math.sign(off);
    const x0 = Math.round(j.neck.x) + (dir > 0 ? 2 : -3);
    const x1 = Math.round(sh.x) + dir * 5;
    const D = Math.abs(x1 - x0);
    if (D < 2) continue;
    for (let k = 0; k <= D; k++) {
      const t = k / D;
      const f = t < 0.5 ? (0.6 * t) / 0.5 : 0.6 + 0.4 * (1 - Math.sqrt(Math.max(0, 1 - ((t - 0.5) / 0.5) ** 2)));
      out.push([x0 + dir * k, topY - 8, topY + Math.round(drop * f)]);
    }
  }
  return out;
}

/** A body or garment piece drawn with the shoulder cut clipped away (whole pixels, no soft edges). */
function sloped(q: Piece, sk: Skeleton, female: boolean): Piece {
  const cut = shoulderCut(sk, female);
  if (!cut.length) return q;
  return {
    ...q,
    draw: (g) => {
      g.save();
      g.beginPath();
      g.rect(-1000, -1000, 3000, 3000);
      for (const [x, y0, y1] of cut) if (y1 > y0) g.rect(x, y0, 1, y1 - y0);
      g.clip('evenodd');
      q.draw(g);
      g.restore();
    },
  };
}

/** Layers drawn over the body that the shoulder line applies to (not the head's, nor what is held or carried). */
const UNSLOPED = new Set(['hair', 'face', 'weapon', 'items', 'tool', 'ornaments2x']);
const GARMENTS = new Set(['top', 'robe', 'cape', 'stole', 'pauldron']);

const lum = (h: string) => {
  const n = parseInt(h.slice(1, 7), 16);
  return 0.3 * (n >> 16) + 0.59 * ((n >> 8) & 255) + 0.11 * (n & 255);
};

/** Whether a piece takes its depth from a getter (newBody.ts arms and mitten hands: `armZ`). */
const zGetter = (q: Piece): boolean => !!Object.getOwnPropertyDescriptor(q, 'z')?.get;

/**
 * A leg piece of the body layer (drawn at its hip's depth, not an arm's). The new body gives its arms and hands a depth
 * getter and its legs a plain one, so there an arm is told by the getter: a turned stance can put a hip at a shoulder's
 * depth (ns-art POSES-R3 fix 1: the woman's guard leg was taken for an arm, kept its skin and showed through the robe).
 * Bodies without getters keep the depth test.
 */
function legOf(q: Piece, sk: Skeleton, getters: boolean): boolean {
  const j = sk.joints;
  if (q.hand) return false;
  const arm = getters ? zGetter(q) : q.z === j.shoulderA.z || q.z === j.shoulderB.z;
  return !arm && (q.z === j.hipA.z || q.z === j.hipB.z);
}

/**
 * A leg drawn with its skin tones swapped for cloth (light and shade kept apart); with `above`, only the rows above it
 * (art px: a child's shorts end at the knee).
 */
function clothedLeg(q: Piece, u: { skin: string[]; cloth: string }, above = Infinity, from = -Infinity): Piece {
  const dark = shade(u.cloth, -0.12);
  return {
    ...q,
    draw: (g) => {
      let want = '';
      const proxy = new Proxy(g, {
        set(t, p, v) {
          if (p === 'fillStyle' && typeof v === 'string') want = v;
          (t as unknown as Record<string | symbol, unknown>)[p] = v;
          return true;
        },
        get(t, p) {
          if (p === 'fillRect')
            return (x: number, y: number, w: number, h: number) => {
              // Every tone of the bare leg (skin, its shades and lights): the lighter half cloth, the darker its shade.
              if (want.startsWith('#') && y < above && y >= from) t.fillStyle = lum(want) >= lum(u.skin[0]!) - 12 ? u.cloth : dark;
              t.fillRect(x, y, w, h);
              if (want) t.fillStyle = want;
            };
          const v = (t as unknown as Record<string | symbol, unknown>)[p];
          return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(t) : v;
        },
      });
      q.draw(proxy);
    },
  };
}

/**
 * The hero's neck at its own width (skullB3.ts `neckWidth`, FACE-HERO R2): the torso piece (figure.ts `torso`) draws the
 * neck as three rects (the column, the chin's shadow row, the shaded side); those are redrawn wider or narrower about the
 * neck joint, everything else passes through.
 */
function heroNeck(q: Piece, sk: Skeleton, sex: 'm' | 'f'): Piece {
  const j = sk.joints;
  const top = Math.round(j.neck.y) - 2;
  const h = Math.max(1, Math.round(j.chest.y) - top);
  return {
    ...q,
    draw: (g) => {
      let from: { x: number; w: number; nx: number; nw: number } | null = null;
      const proxy = new Proxy(g, {
        get(t, p) {
          if (p === 'fillRect')
            return (x: number, y: number, w: number, hh: number) => {
              if (!from && y === top && hh === h && (w === 3 || w === 4) && x === Math.round(j.neck.x - (w === 4 ? 2 : 1))) {
                const nw = mockNeck(sk) ?? neckWidth(sex, w);
                from = { x, w, nx: Math.round(j.neck.x - Math.floor(nw / 2)), nw };
                // 0: the body mock draws its own profile neck (bodyMockB3.ts `mockSkullNeck`).
                return nw ? t.fillRect(from.nx, y, nw, hh) : undefined;
              }
              if (from && x === from.x && y === top + 2 && w === from.w && hh === 1) return from.nw ? t.fillRect(from.nx, y, from.nw, 1) : undefined;
              if (from && x === from.x && y === top && w === 1 && hh === h) return from.nw ? t.fillRect(from.nx, y, 1, hh) : undefined;
              return t.fillRect(x, y, w, hh);
            };
          const v = (t as unknown as Record<string | symbol, unknown>)[p];
          return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(t) : v;
        },
        set(t, p, v) {
          (t as unknown as Record<string | symbol, unknown>)[p] = v;
          return true;
        },
      });
      q.draw(proxy);
    },
  };
}

/** The layers of an appearance for B3: the head's art shrunk, the face and the redrawn items swapped in. */
export function b3Layers(app: Appearance, p: B3, expr: Expr | 'auto' = 'auto'): Layer[] {
  const s = p.s;
  return app.layers.map((l): Layer => {
    const id = l.id;
    if (id === 'face' && app.look) {
      const face = faceLayerB3(app.look, expr);
      return {
        ...face,
        pieces: (sk, fit) =>
          face.pieces(sk, fit).map((q) => headPiece(q, sk, s)),
      };
    }
    if (id.startsWith('headwear:')) {
      const redrawn = REDRAWN_HEADWEAR[id.slice('headwear:'.length)];
      if (redrawn) return redrawn();
      return { ...l, pieces: (sk, fit) => l.pieces(sk, fit).map((q) => headPiece(q, sk, s)) };
    }
    if (id === 'hair')
      return {
        ...l,
        pieces: (sk, fit) =>
          l.pieces(sk, fit).map((q) => mockHairBack(headPiece(q, sk, s, q.z === -0.95 || (sk.back && Math.abs(q.z - (sk.joints.head.z + 0.03)) < 1e-6)), sk, !!app.body.female)),
      };
    const female = !!app.body.female;
    // Dressed, the skin under a garment is cut with it so none peeks out at the shoulder; bare, the body keeps its own
    // sloped shoulder and deltoid.
    const dressed = app.layers.some((x) => GARMENTS.has(x.id));
    // A woman's long robe (ruqun): the legs under it drawn in the robe's own cloth (QA B3-R2: a darker underskirt read as a black wedge), so a stride or a crouch that reaches
    // past the robe's side shows cloth, not a patch of skin (QA B3-R1).
    const underRobe =
      app.body.female && app.look && app.layers.some((x) => x.id === 'robe')
        ? { skin: [app.look.skin, app.look.skinS].map((c) => c.toLowerCase()), cloth: app.look.coat?.c ?? (app.look.hanfu ? '#1d2a4f' : (app.look.outer ?? '#3a3440')) }
        : null;
    // A child's bare thighs read as underwear at game size (ns-art B3): shorts to the knee in the outfit's colour.
    const shorts =
      app.body.age === 'child' && app.look && !app.layers.some((x) => x.id === 'bottom' || x.id === 'robe')
        ? { skin: [app.look.skin, app.look.skinS].map((c) => c.toLowerCase()), cloth: shade(app.look.coat?.c ?? app.look.outer ?? '#7a4a3a', -0.3) }
        : null;
    if (id === 'body')
      return {
        ...l,
        pieces: (sk, fit) => {
          const given = l.pieces(sk, fit);
          // Dressed figures' art-resolution mitten hands as full-resolution hands (handsB3.ts, review toggle).
          const raw = app.look ? mockHands(withFineHands(given, sk, app.look, zGetter), sk, female, app.look, zGetter) : given;
          // The player's man and woman: the hand-drawn fine skull (skullB3.ts, FACE-HERO-R1) instead of the shrunk head.
          const hero = heroSkullFor(app.look, app.body.age);
          const getters = given.some(zGetter);
          // Only from the waist down (a torso at a hip's depth keeps its skin at the collar).
          const waist = Math.round(sk.joints.waist.y);
          return raw
            .map((q) =>
              underRobe && legOf(q, sk, getters)
                ? clothedLeg(q, underRobe, Infinity, waist)
                : shorts && legOf(q, sk, getters)
                  ? clothedLeg(q, shorts, Math.max(sk.joints.kneeA.y, sk.joints.kneeB.y) + 1, waist)
                  : q,
            )
            .map((q) => (hero && !q.hand && q.z === sk.joints.chest.z ? heroNeck(q, sk, hero) : q))
            .map((q) => (app.look ? mockFarLeg(q, sk, female, app.look, legOf(q, sk, getters)) : q))
            .map((q) =>
            q.z === sk.joints.head.z && !q.fine && !q.hand
              ? hero && app.look
                ? mockSkullNeck(heroSkullPiece(sk, hero, app.look), sk, hero, app.look)
                : headPiece(q, sk, s)
              : SLOPE && (dressed || bodyMockOf()) && !q.hand
                ? sloped(q, sk, female)
                : q,
          )
            .map((q) => (q.z === sk.joints.head.z ? q : mockProfile(q, sk)))
            .concat(app.look ? mockExtras(sk, female, app.look) : []);
        },
      };
    if (id === 'ornaments2x')
      return {
        ...l,
        pieces: (sk, fit) => {
          const hz = sk.joints.head.z;
          const sc = (a: readonly [number, number]) => [a[0] * s, a[1] * s] as const;
          return l.pieces(sk, fit).map((q) => {
            if (Math.abs(q.z - (hz + 0.04)) < 1e-6) return crownB3(sk, sc(fit.anchors.crown));
            if (Math.abs(q.z - (hz + 0.045)) < 1e-6) return hairpinB3(sk, sc(fit.anchors.buns[0] ?? fit.anchors.crown));
            return q;
          });
        },
      };
    if (SLOPE && !UNSLOPED.has(id))
      return { ...l, pieces: (sk, fit) => l.pieces(mockGarment(sk, female, id), fit).map((q) => (q.hand ? q : sloped(q, sk, female))) };
    return l;
  });
}

const LAYERS = new WeakMap<Appearance, Layer[]>();

/** rig/sets.ts `frameOf` at B3. */
export function frameOfB3(
  app: Appearance,
  view: View8,
  pose: Pose,
  ph = 0,
  lag = 0,
  blink = false,
  /** Review sheets: a fixed expression. */
  expr?: Expr,
): { c: HTMLCanvasElement; blades: BladeLine[] } {
  const p = b3Of(app.body);
  // Body A (bodyMockB3.ts) only for the player's man and woman.
  setBodyTarget(!!app.look?.faceId?.startsWith('hero') && (app.body.age ?? 'adult') === 'adult');
  const bare = bodyMock(b3Skeleton(newSkeleton(skeleton(view, pose, ph, lag, app.body, blink)), p), app.body);
  const cloth = app.look?.cloth ? clothAt(pose, ph, app.body) : undefined;
  const sk = cloth ? withCloth(bare, cloth, lag) : bare;
  let layers = expr ? b3Layers(app, p, expr) : LAYERS.get(app);
  if (!layers) LAYERS.set(app, (layers = b3Layers(app, p)));
  const raw = compose(layers, sk, { res: RIG_RES });
  // The hero skull keeps its own skin through the finish (skullB3.ts `restoreSkull`).
  const hero = RIG_RES === 2 ? heroSkullFor(app.look, app.body.age) : null;
  const before = hero ? new Uint32Array(raw.getContext('2d')!.getImageData(0, 0, raw.width, raw.height).data.buffer) : null;
  const done = sk.left ? leftFinish(raw) : litFinish(raw);
  // Body mock (review only): turned three-quarter, enclosed slivers between the overlapping legs closed as a seam; rows
  // from the crotch down (PAD_TOP 16, sprites.ts). Head-on the gaps between arm and waist and between the thighs are meant.
  if (app.look && (sk.view === 'front3' || sk.view === 'back3') && !app.layers.some((l) => GARMENTS.has(l.id) || l.id === 'bottom' || l.id === 'shoes'))
    fillHoles(done, (Math.round(Math.max(sk.joints.hipA.y, sk.joints.hipB.y)) + 16) * 2, shade(app.look.skin, -0.04));
  if (hero && before && app.look) restoreSkull(before, done.getContext('2d')!, raw.width, sk, hero, app.look);
  const c = cropFrame(done);
  return { c: density1x() ? toArtRes(c) : c, blades: app.weapon ? bladeLines(app.weapon, sk) : [] };
}
